// frontend/src/hooks/useRiderBookingWebSocket.jsx

import { useState, useEffect, useRef, useCallback } from 'react';
import { createAuthenticatedWebSocket } from '../utils/webSocketAuth';
import {
    classifyWebSocketClose,
    getTokenSessionId,
    handleTerminalSessionFailure,
} from '../utils/authUtils';
import logger from '../utils/logger';

/**
 * Custom hook for rider booking real-time updates via WebSocket
 * Connects to /ws/entities/{entityId}/rider/bookings
 *
 * SECURITY: Passes JWT token for server-side validation
 * Only receives updates for the authenticated user's own bookings
 *
 * @param {number} entityId - Entity ID to subscribe to
 * @param {boolean} shouldConnect - Whether to connect (set to false for admins who use admin WebSocket)
 * @param {Function} onBookingEvent - Callback when booking event received
 * @returns {{ connectionStatus: string, isConnected: boolean }}
 */
export const useRiderBookingWebSocket = (entityId, shouldConnect, onBookingEvent) => {
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const wsRef = useRef(null);
    const pingIntervalRef = useRef(null); // Track ping interval for cleanup
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const denialLatchRef = useRef(null);
    const bookingEventRef = useRef(onBookingEvent);
    const maxReconnectAttempts = 5;
    const baseReconnectDelay = 3000;

    // IMPORTANT: Use ref to store callback to prevent reconnection loops
    useEffect(() => {
        bookingEventRef.current = onBookingEvent;
    }, [onBookingEvent]);

    // Get WebSocket URL. JWT auth is sent with a WebSocket subprotocol so it
    // does not appear in URLs, browser history, proxies, or URL logs.
    const getWsUrl = useCallback(() => {
        const baseWsUrl = import.meta.env.VITE_WS_URL || window.location.origin.replace('http', 'ws');
        return `${baseWsUrl}/ws/entities/${entityId}/rider/bookings`;
    }, [entityId]);

    // Handle incoming messages - uses ref to avoid dependency on callback
    const handleMessage = useCallback((event, onAuthorized) => {
        try {
            const message = typeof event.data === 'string' ? event.data.trim() : event.data;

            // The backend heartbeat response is intentionally plain text, not a
            // JSON event envelope. Handle that narrow keepalive path before
            // parsing so expected `pong` traffic does not look like a broken
            // booking payload.
            if (message === 'pong') {
                return;
            }

            const data = JSON.parse(event.data);

            if (data.type === 'connected') {
                onAuthorized();
                return;
            }

            // Handle booking status change events for riders - use ref.current
            if (
                data.type === 'booking_status_changed'
                || data.type === 'operational_unavailability_changed'
            ) {
                if (bookingEventRef.current) {
                    bookingEventRef.current({
                        type: data.type,
                        ...data.payload
                    });
                }
            }
        } catch {
            // Parser errors can echo untrusted frame content, so neither the
            // exception nor its message is safe to log here.
            logger.error('Error parsing rider booking WebSocket message', {
                message: 'Invalid WebSocket message payload'
            });
        }
    }, []); // No dependencies - stable reference

    // Connect to WebSocket
    const connect = useCallback(() => {
        if (!entityId || !shouldConnect) return;

        // Avoid noisy reconnect loops if auth token is missing
        const token = localStorage.getItem('token');
        if (!token) {
            setConnectionStatus('error');
            console.warn('Rider booking WebSocket skipped - missing auth token');
            return;
        }

        // JWT rotation retains jti, so use the database-session identity for
        // the latch. A new bearer for the same session must not authorize
        // another policy retry.
        const denialKey = `${getTokenSessionId(token) || token}:${entityId}`;
        if (denialLatchRef.current === denialKey) {
            setConnectionStatus('access_denied');
            return;
        }
        denialLatchRef.current = null;

        // Avoid reconnect loops if we're already connected/connecting
        if (wsRef.current) {
            if ([WebSocket.OPEN, WebSocket.CONNECTING].includes(wsRef.current.readyState)) {
                return;
            }
            wsRef.current = null;
        }

        setConnectionStatus('connecting');

        const wsUrl = getWsUrl();
        const authBearer = token;
        const ws = createAuthenticatedWebSocket(wsUrl, authBearer);
        wsRef.current = ws;

        ws.onopen = () => {
            if (wsRef.current !== ws) return;
            // Browser transport state is not authorization state. The server's
            // `connected` frame is emitted only after session, membership, and
            // endpoint-role checks complete.
            console.log('Rider booking WebSocket transport opened');
        };

        ws.onmessage = (event) => {
            if (wsRef.current === ws) {
                handleMessage(event, () => {
                    setConnectionStatus('connected');
                    reconnectAttemptsRef.current = 0;
                    if (!pingIntervalRef.current) {
                        pingIntervalRef.current = setInterval(() => {
                            if (wsRef.current === ws && ws.readyState === WebSocket.OPEN) {
                                ws.send('ping');
                            }
                        }, 30000);
                    }
                });
            }
        };

        ws.onerror = (error) => {
            if (wsRef.current !== ws) return;
            console.error('Rider booking WebSocket error:', error);
            setConnectionStatus('error');
        };

        ws.onclose = (event) => {
            if (wsRef.current !== ws) return;
            console.log('Rider booking WebSocket closed:', event.code, event.reason);
            setConnectionStatus('disconnected');
            wsRef.current = null;

            // IMPORTANT: Clear ping interval on ANY close to prevent orphaned intervals
            if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
            }

            const closeClassification = classifyWebSocketClose(event.code, event.reason);
            if (closeClassification.category === 'session_invalid') {
                if (reconnectTimeoutRef.current) {
                    clearTimeout(reconnectTimeoutRef.current);
                    reconnectTimeoutRef.current = null;
                }
                setConnectionStatus('auth_expired');
                void handleTerminalSessionFailure({
                    expectedToken: authBearer,
                    correlation: closeClassification.correlation,
                });
                return;
            }

            if (['static_denial', 'mutable_denial'].includes(closeClassification.category)) {
                denialLatchRef.current = denialKey;
                setConnectionStatus('access_denied');
                return;
            }

            // Attempt reconnection if not intentionally closed
            if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
                const delay = baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
                reconnectAttemptsRef.current++;

                console.log(`Rider WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`);
                setConnectionStatus('reconnecting');

                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, delay);
            }
        };

    }, [entityId, shouldConnect, getWsUrl, handleMessage]);

    // Disconnect - cleans up all resources
    const disconnect = useCallback(() => {
        // Clear reconnect timeout
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        // Clear ping interval
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }

        // Close WebSocket
        if (wsRef.current) {
            wsRef.current.close(1000, 'Component unmounting');
            wsRef.current = null;
        }

        setConnectionStatus('disconnected');
    }, []);

    // Connect on mount, disconnect on unmount
    useEffect(() => {
        if (entityId && shouldConnect) {
            connect();
        }

        return () => {
            disconnect();
        };
    }, [entityId, shouldConnect, connect, disconnect]);

    // Handle page visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page hidden - disconnect to save resources
                disconnect();
            } else {
                // Page visible - reconnect
                if (entityId && shouldConnect) {
                    reconnectAttemptsRef.current = 0;
                    connect();
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [entityId, shouldConnect, connect, disconnect]);

    return {
        connectionStatus,
        isConnected: connectionStatus === 'connected'
    };
};

export default useRiderBookingWebSocket;
